import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import Swal from 'sweetalert2';

import { SCUIButton, SCUINoRecordComponent, SCUILoadingLinear, SCUIDateRanger } from '../../atomic/atoms';
import { SCUIBreadCrumb, SCUICustomDataGrid, SCUIGeneralDrawer } from '../../atomic/molecules';
import { SCUIContent } from '../../atomic/organisms';
import { Grid, FormControl, MenuItem, Select, InputLabel, Tooltip, TextField } from '@mui/material';
import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';

import { RefundQuery } from '../../services/query/';
import { RefundRestQuery } from '../../services/query/';
import Helper from '../../utils/helper';
import TimeHelper from '../../utils/time';

import cashPayment from '../../assets/icons/svg/pageIcons/cashPayment.svg';
import SCUIDownloadButton from '../../atomic/atoms/SCUIDownloadButton/SCUIDownloadButton';
import { PaymentMerchantTypeNames } from '../../types/enums';
import { RLV2 } from './refundListV2.classes';
import { applyRefundUrlSearchParams, hasRefundUrlSearchParams } from './refundListV2.helpers';

const selectedCountry: any = JSON.parse(localStorage.getItem('selectedCountry') || '{}');

export const RefundListV2: FC<any> = () => {
    const [refundData, setRefundData] = useState<any>();
    const [refundStatusId, setRefundStatusId] = useState(-1);
    const [refundStatusList, setRefundStatusList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [drawerEditOpen, setDrawerEditOpen] = useState(false);
    const [drawerAddOpen, setDrawerAddOpen] = useState(false);
    const [createdDate, setCreatedDate] = useState(moment().subtract(14, 'days').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(moment().add(1, 'days').format('YYYY-MM-DD'));
    const [refundId, setRefundId] = useState('');
    const [orderNumber, setOrderNumber] = useState('');
    const [excelDatas, setExcelDatas] = useState<any>();

    const location = useLocation();
    const search = location.search;
    const searchParams = new URLSearchParams(search);
    const { t } = useTranslation();
    const query = new URLSearchParams(useLocation().search);
    const orderNumberFromUrl = query.get('orderNumber');
    const history = useHistory();

    const GetRefundStatusList = () => {
        RefundQuery.GetRefundStatusList().then((res: any) => {
            let parseRefundStatusList = res
                .map((item: any) => {
                    return item.refundStatusId !== -1
                        ? { key: Helper.getRefundStatusName(item.refundStatusId), value: item.refundStatusId }
                        : null;
                })
                .filter((item: any) => item !== null);
            setRefundStatusList(parseRefundStatusList);
        });
    };

    const _handlChangeDate = (event: any) => {
        setCreatedDate(moment(event[0], 'DD/MM/YYYY').format('YYYY-MM-DD'));
        setEndDate(moment(event[1], 'DD/MM/YYYY').format('YYYY-MM-DD'));
    };

    useEffect(() => {
        GetRefundStatusList();
    }, []);

    useEffect(() => {
        if (refundData) {
            buildExcelDatas(refundData);
        }
    }, [refundData]);

    let req: any = {};
    let refundStatusParameter = Number(searchParams.get('RefundStatus')) || Number(searchParams.get('refundStatus'));
    let refundIdParameter = searchParams.get('RefundId') || searchParams.get('refundId');
    let orderNumberParameter = searchParams.get('OrderNumber') || searchParams.get('orderNumber');
    let createdDateParameter = searchParams.get('StartDate') || searchParams.get('startDate');
    let endDateParameter = searchParams.get('EndDate') || searchParams.get('endDate');
    useEffect(() => {
        if (search.length > 0) {
            const urlParams = {
                refundStatusParameter,
                refundIdParameter,
                orderNumberParameter,
                createdDateParameter,
                endDateParameter,
            };
            if (hasRefundUrlSearchParams(urlParams) && !refundData) {
                req = applyRefundUrlSearchParams(urlParams, {
                    setRefundStatusId,
                    setRefundId,
                    setOrderNumber,
                    setCreatedDate,
                    setEndDate,
                });
            }
            listele(req);
        } else {
            setRefundData(null);
        }
    }, [refundIdParameter, orderNumberParameter, createdDateParameter, endDateParameter, refundStatusParameter]);

    const CallRefundList = (params: any) => {
        setLoading(true);
        RefundRestQuery.RefundList(params)
            .then((res: any) => {
                if (res?.items?.length > 0) {
                    let parseData = res.items.map((item: any) => {
                        return {
                            id: item.refundId,
                            date: TimeHelper.formatDateHourSecond(item.date),
                            customer: item.customerFirstName + ' ' + item.customerLastName,
                            email: item.customerMail,
                            orderNumber: item.orderNumber,
                            rmacode: item.rmaCode || '-',
                            refundReasonDesc: item.refundReasonDesc,
                            refundStatusName: item.refundStatusName,
                            amount: Helper.formatNumber(item.amount),
                            description: item.description,
                            customerId: item.customerId,
                            operatorName:
                                item.operatorFirstName == null && item.operatorLastName == null
                                    ? '-'
                                    : [item.operatorFirstName, item.operatorLastName].filter(Boolean).join(' '),
                            refundReasonId: item.refundReasonId,
                            refundStatusId: item.refundStatusId,
                            refundMethod: item.refundMethod,
                            creditCardTypeName: item.creditCardTypeName || '-',
                            paymentMerchantTypeName: PaymentMerchantTypeNames[item.paymentMerchantTypeId] || '-',
                            goalId: item.goalId,
                            currencyId: item.currencyId,
                            orderDate: item?.orderDate !== null ? TimeHelper.formatDateHour(item?.orderDate) : "-"   , 
                            creditCardNumber : item?.creditCardNumber,
                            provisionNumber : item?.provisionNumber,
                            orderAmount : item?.orderAmount,
                            refundType : item?.refundType

                        };
                    });

                    setRefundData(parseData);
                    setOrderNumber('');
                    setRefundStatusId(-1);
                } else {
                    setOrderNumber('');
                    setRefundStatusId(-1);
                    Swal.fire(t('refundList.noRefundFound'), res.errorMessage, 'error');
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };
    const defaultColumnOptions = {
        filterable: false,
        disableReorder: false,
        sortable: false,
        hideable: false,
        editable: false,
        resizable: false,
        groupable: false,
        disableColumnMenu: true,
    };
    const columns: ExtendedGridColDef[] = [
        {
            field: 'id',
            headerName: t('refundList.refundId'),
            flex: 1,
            minWidth: 100,
            renderCell: (params: any) => {
                return (
                    <div className={RLV2.cellRefundId} style={{ display: 'flex', alignItems: 'center' }}>
                        <a
                            className={RLV2.linkRefundId}
                            style={{ color: 'blue', textDecoration: 'underline' }}
                            target="_blank"
                            rel="noopener noreferrer"
                            href={`/RefundV2/?id=${params.row.id}`}
                        >
                            {params.row.id}
                        </a>
                    </div>
                );
            },
            ...defaultColumnOptions,
        },
        {
            field: 'date',
            headerName: t('refundList.date'),
            flex: 1,
            minWidth: 145,
            renderCell: (params: any) => {
                return (
                    <div className={RLV2.cellDate}>
                        <Tooltip title={params.row.date}>
                            <p>{params.row.date}</p>
                        </Tooltip>
                    </div>
                );
            },
            ...defaultColumnOptions,
        },
        {
            field: 'orderNumber',
            headerName: t('refundList.orderNumber'),
            flex: 1,
            minWidth: 100,
            renderCell: (params: any) => {
                return (
                    <div className={RLV2.cellOrderNumber} style={{ display: 'flex', alignItems: 'center' }}>
                        <a
                            className={RLV2.linkOrderNumber}
                            style={{ color: 'blue', textDecoration: 'underline' }}
                            target="_blank"
                            rel="noopener noreferrer"
                            href={`/orderDetail?OrderNumber=${params.row.orderNumber}`}
                        >
                            {params.row.orderNumber}
                        </a>
                    </div>
                );
            },
            ...defaultColumnOptions,
        },
        {
            field: 'rmacode',
            headerName: t('refundList.rmacode'),
            flex: 1,
            renderCell: (params: any) => {
                return (
                    <div className={RLV2.cellRmaCode}>
                        <Tooltip title={params.row.rmacode}>
                            <p>{params.row.rmacode}</p>
                        </Tooltip>
                    </div>
                );
            },
            ...defaultColumnOptions,
        },
        {
            field: 'refundReasonDesc',
            headerName: t('refundList.refundReasonDesc'),
            flex: 1,
            renderCell: (params: any) => {
                return (
                    <div className={RLV2.cellRefundReason}>
                        <Tooltip title={Helper.getRefundReasonName(params.row.refundReasonId)}>
                            <p>{Helper.getRefundReasonName(params.row.refundReasonId)}</p>
                        </Tooltip>
                    </div>
                );
            },
            ...defaultColumnOptions,
        },
        {
            field: 'refundStatusName',
            headerName: t('refundList.refundStatusName'),
            flex: 1,
            renderCell: (params: any) => {
                return (
                    <div className={RLV2.cellRefundStatus}>
                        <Tooltip title={Helper.getRefundStatusName(params.row.refundStatusId)}>
                            <p>{Helper.getRefundStatusName(params.row.refundStatusId)}</p>
                        </Tooltip>
                    </div>
                );
            },
            ...defaultColumnOptions,
        },
        {
            field: 'refundMethod',
            headerName: t('refundList.refundMethod'),
            flex: 1,
            renderCell: (params: any) => {
                return (
                    <div className={RLV2.cellRefundMethod}>
                        <Tooltip title={params.row.refundMethod}>
                            <p>{params.row.refundMethod}</p>
                        </Tooltip>
                    </div>
                );
            },
            ...defaultColumnOptions,
        },
        {
            field: 'creditCardTypeName',
            headerName: t('refundList.creditCardTypeName'),
            flex: 1,
            renderCell: (params: any) => {
                return (
                    <div className={RLV2.cellCreditCardType}>
                        <Tooltip title={params.row.creditCardTypeName}>
                            <p>{params.row.creditCardTypeName}</p>
                        </Tooltip>
                    </div>
                );
            },
            ...defaultColumnOptions,
        },
        {
            field: 'paymentMerchantTypeName',
            headerName: t('refundList.paymentMerchantTypeId'),
            flex: 1,
            renderCell: (params: any) => {
                return (
                    <div className={RLV2.cellPaymentMerchantType}>
                        <Tooltip title={params.row.paymentMerchantTypeName}>
                            <p>{params.row.paymentMerchantTypeName}</p>
                        </Tooltip>
                    </div>
                );
            },
            ...defaultColumnOptions,
        },
        {
            field: 'amount',
            headerName: t('refundList.amount'),
            flex: 1,
            renderCell: (params: any) => {
                const currencySymbol =
                    (selectedCountry?.supportedCurrencies || []).find(
                        (curr: any) => curr.currencyId === params.row.currencyId
                    )?.shortCode || '';
                return `${(Math.trunc(parseFloat(params.value?.replace(',', '.')) * 100) / 100).toFixed(
                    2
                )} ${currencySymbol}`;
            },
            ...defaultColumnOptions,
        },
        {
            field: 'operatorName',
            headerName: t('refundList.operatorName'),
            flex: 1,
            minWidth: 110,
            renderCell: (params: any) => {
                return (
                    <div
                        className={RLV2.cellOperator}
                        style={{ whiteSpace: 'normal', wordWrap: 'break-word', lineHeight: '1.2' }}
                    >
                        <Tooltip title={params.row.operatorName}>
                            <p style={{ margin: 0 }}>{params.row.operatorName}</p>
                        </Tooltip>
                    </div>
                );
            },
            ...defaultColumnOptions,
        },
    ];
    useEffect(() => {
        if (orderNumberFromUrl) {
            setOrderNumber(orderNumberFromUrl);
            let params = { orderNumber: orderNumberFromUrl };
            CallRefundList(params);
        }
    }, [orderNumberFromUrl]);
    const listele = async (request?: any) => {
        if (request !== null) {
            CallRefundList(request);
        } else {
            let refundId_t = refundId ? Number(refundId) : 0;
            if (Number(refundId_t) === 0 && !orderNumber && refundStatusId === -1)
                Swal.fire(t('refundList.selectStatus'), '', 'error');
            else if (
                Number(refundId_t) === 0 &&
                orderNumber.length === 0 &&
                (createdDate.trim().length === 0 || endDate.trim().length === 0)
            )
                Swal.fire(t('refundList.selectDate'), '', 'error');
            else {
                setLoading(true);
                try {
                    let params: any = BuildRequest(
                        refundStatusId,
                        refundId_t,
                        refundId,
                        orderNumber,
                        createdDate,
                        endDate
                    );
                    CallRefundList(params);
                    setLoading(false);
                } catch (error: any) {
                    Swal.fire('Error', error.toString(), 'error');
                }
            }
        }
    };
    const handleKeyPress = (event: any) => {
        if (event.key === 'Enter') {
            listele(null);
        }
    };
    const buildExcelDatas = (refundData_: any) => {
        const downloadedData = refundData_.map((item: any) => {
            const currencySymbol =
                (selectedCountry?.supportedCurrencies || []).find((curr: any) => curr.currencyId === item.currencyId)
                    ?.shortCode || '';
            return {
                refundId: item.id,
                goalId: item.goalId || '-',
                date: item.date || '-',
                orderNumber: item.orderNumber || '-',
                rmaCode: item.rmacode || '-',
                refundReason: Helper.getRefundReasonName(item.refundReasonId) || '-',
                refundStatus: Helper.getRefundStatusName(item.refundStatusId) || '-',
                refundMethod: item.refundMethod || '-',
                creditCardTypeName: item.creditCardTypeName || '-',
                paymentMerchantTypeId: item.paymentMerchantTypeName || '-',
                amount:
                    `${(Math.trunc(parseFloat(item.amount?.replace(',', '.')) * 100) / 100).toFixed(
                        2
                    )} ${currencySymbol}` || '-',
                operator: item.operatorName || '-',
                            orderDate:item?.orderDate || '-',
                            creditCardNumber : item?.creditCardNumber|| '-',
                            provisionNumber : item?.provisionNumber|| '-',
                            orderAmount : item?.orderAmount|| '-',
                            refundType : item?.refundType|| '-'
            };
        });
        setExcelDatas(downloadedData);
    };
    return (
        <SCUIContent className={RLV2.page}>
            <SCUIGeneralDrawer
                open={drawerEditOpen}
                width={'400px'}
                paperClassName={RLV2.editDrawerPaper}
                onClose={(event: any, reason: any) => {
                    if (reason === 'backdropClick') {
                        setDrawerEditOpen(false);
                    }
                }}
            ></SCUIGeneralDrawer>
            <SCUIGeneralDrawer
                open={drawerAddOpen}
                width={'400px'}
                paperClassName={RLV2.addDrawerPaper}
                onClose={(event: any, reason: any) => {
                    if (reason === 'backdropClick') {
                        setDrawerAddOpen(false);
                    }
                }}
            ></SCUIGeneralDrawer>
            <Grid
                size={{
                    sm: 12,
                    md: 12,
                    lg: 12,
                    xl: 12,
                }}
            >
                <Grid
                    className={RLV2.breadcrumbSection}
                    container
                    direction="row"
                    spacing={2}
                    sx={{ marginLeft: '0.5rem' }}
                >
                    <SCUIBreadCrumb
                        title={t('customerInformation.returnPaymentList')}
                        componentLink={'/refundListV2'}
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                            setRefundData([]);
                            setEndDate('');
                            setRefundId('');
                            setOrderNumber('');
                            setEndDate(moment().add(1, 'days').format('YYYY-MM-DD'));
                            setCreatedDate(moment().subtract(14, 'days').format('YYYY-MM-DD'));
                            setRefundStatusId(-1);
                        }}
                    />
                    <div className={RLV2.loadingSection}>
                        <SCUILoadingLinear display={loading} />
                    </div>
                </Grid>
                <Grid
                    size={{
                        sm: 12,
                        md: 12,
                        lg: 12,
                        xl: 12,
                    }}
                >
                    <Grid
                        size={{
                            sm: 12,
                            md: 12,
                            lg: 12,
                            xl: 12,
                        }}
                    >
                        {refundData && refundData.length > 0 ? (
                            <Grid
                                className={RLV2.resultsSection}
                                size={{
                                    sm: 12,
                                    md: 12,
                                    lg: 12,
                                    xl: 12,
                                }}
                            >
                                <div className={RLV2.downloadButtonWrap}>
                                    <SCUIDownloadButton
                                        position="right"
                                        excelData={{
                                            excelDownloadData: excelDatas,
                                            hiddenExcelColumn: ['id'],
                                            excelDownloadFolderName: t('excel.refundListV2'),
                                        }}
                                    />
                                </div>
                                <div className={RLV2.dataGridWrap}>
                                    <SCUICustomDataGrid
                                        sx={{
                                            '& .MuiDataGrid-cell': {
                                                fontSize: '10px',
                                            },
                                            '& .MuiDataGrid-columnHeaderTitle': {
                                                fontSize: '10px',
                                            },
                                        }}
                                        randomId
                                        rows={refundData}
                                        columns={columns}
                                        excelDownloadButton={false}
                                        excelDownloadButtonName={t('general.exportToExcel')}
                                        excelDownloadData={excelDatas}
                                        excelDownloadFolderName={'RefundList'}
                                    />
                                </div>
                            </Grid>
                        ) : (
                            <div
                                className={RLV2.emptyState}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignContent: 'center',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                <div className={RLV2.emptyNoRecord}>
                                    <SCUINoRecordComponent
                                        text={t('refundList.makeSearch')}
                                        height={'140px'}
                                        width={'140px'}
                                        fontSize="12px"
                                        img={cashPayment}
                                    />
                                </div>
                                <FormControl
                                    className={RLV2.filterForm}
                                    style={{
                                        width: 520,
                                        marginTop: '1.5rem',
                                        marginBottom: '0',
                                    }}
                                >
                                    <TextField
                                        className={RLV2.inputRefundId}
                                        label={t('refundList.refundId')}
                                        defaultValue={refundId}
                                        onChange={(e) => setRefundId(e.target.value)}
                                    />
                                </FormControl>
                                <FormControl
                                    className={RLV2.filterForm}
                                    style={{
                                        width: 520,
                                        marginTop: '1.5rem',
                                        marginBottom: '0',
                                    }}
                                >
                                    <TextField
                                        className={RLV2.inputOrderNumber}
                                        label={t('customerInformation.orderNumber')}
                                        defaultValue={orderNumber}
                                        onChange={(e) => setOrderNumber(e.target.value)}
                                        onKeyDown={handleKeyPress}
                                    />
                                </FormControl>
                                <FormControl className={RLV2.filterForm} style={{ width: 520, marginTop: '1.5rem' }}>
                                    <InputLabel id="labelApp">{t('refundList.paymentStatus')}</InputLabel>
                                    <Select
                                        className={RLV2.selectRefundStatus}
                                        label={t('refundList.paymentStatus')}
                                        labelId="labelApp"
                                        id="parentMenuLabel"
                                        value={refundStatusId}
                                        onChange={(event) => {
                                            setRefundStatusId(Number(event.target.value));
                                        }}
                                        fullWidth
                                    >
                                        {refundStatusList &&
                                            refundStatusList.map((dist: any) => (
                                                <MenuItem
                                                    className={RLV2.selectRefundStatusItem}
                                                    key={dist.value}
                                                    id={dist.value}
                                                    value={dist.value}
                                                >
                                                    {t(dist.key)}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                                <div
                                    className={RLV2.dateRangeWrap}
                                    style={{ width: 520, marginTop: '1.5rem', marginBottom: '0' }}
                                >
                                    <SCUIDateRanger
                                        fromDate={moment().subtract(14, 'days').format('DD/MM/YYYY')}
                                        toDate={moment().add(1, 'days').format('DD/MM/YYYY')}
                                        setDateRange={_handlChangeDate}
                                        dateFormat="DD/MM/YYYY"
                                        fullWidth
                                    />
                                </div>
                                <FormControl className={RLV2.filterForm} style={{ width: 520, marginTop: '1.5rem' }}>
                                    <SCUIButton
                                        className={RLV2.filterButton}
                                        text={t('general.filter')}
                                        type="primary"
                                        sizeType="default"
                                        size={'l'}
                                        onClick={() =>
                                            history.push(
                                                '/refundListV2?' +
                                                    Helper.buildURLFromObject(
                                                        BuildRequest(
                                                            refundStatusId,
                                                            refundId ? Number(refundId) : 0,
                                                            refundId,
                                                            orderNumber,
                                                            createdDate,
                                                            endDate
                                                        )
                                                    )
                                            )
                                        }
                                    />
                                </FormControl>
                            </div>
                        )}
                    </Grid>
                </Grid>
            </Grid>
        </SCUIContent>
    );
};
function BuildRequest(
    refundStatusId: number,
    refundId_t: number,
    refundId: string,
    orderNumber: string,
    createdDate: string,
    endDate: string
) {
    let params: any = {};
    params.RefundStatus = refundStatusId;
    if (Number(refundId_t) > 0) {
        params.refundId = refundId;
    }
    if (orderNumber) {
        params.orderNumber = orderNumber;
    }
    if (createdDate) {
        params.StartDate = createdDate;
    }
    if (endDate) {
        params.EndDate = endDate;
    }
    return params;
}
